import React, { useEffect, useState } from 'react';
import { Bell, AlertTriangle, Info, ExternalLink, ChevronDown, ChevronUp, Filter, CheckCircle2 } from 'lucide-react';
import api from '../../api/client';
import { useNavigate } from 'react-router-dom';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

interface Alert {
  type: 'critical' | 'warning' | 'info';
  title: string;
  message: string;
  entity: 'vacancy' | 'candidate';
  entityId: string;
  createdAt: string;
}

const alertConfig = {
  critical: { icon: <AlertTriangle size={20} />, color: '#ef4444', bg: 'rgba(239,68,68,0.1)', label: 'Критический' },
  warning: { icon: <AlertTriangle size={20} />, color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', label: 'Предупреждение' },
  info: { icon: <Info size={20} />, color: '#3b82f6', bg: 'rgba(59,130,246,0.1)', label: 'Информация' },
};

const AlertsPage: React.FC = () => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterEntity, setFilterEntity] = useState('all');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const res = await api.get('/recruitment-analytics/alerts');
        setAlerts(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAlerts();
  }, []);

  if (loading) return <div style={{ padding: 60, textAlign: 'center' }}>Загрузка...</div>;

  // Подсчёты для KPI и графиков
  const total = alerts.length;
  const criticalCount = alerts.filter(a => a.type === 'critical').length;
  const warningCount = alerts.filter(a => a.type === 'warning').length;
  const infoCount = alerts.filter(a => a.type === 'info').length;

  const typeChartData = [
    { name: 'Критические', value: criticalCount, color: '#ef4444' },
    { name: 'Предупреждения', value: warningCount, color: '#f59e0b' },
    { name: 'Информация', value: infoCount, color: '#3b82f6' },
  ].filter(d => d.value > 0);

  const vacancyAlerts = alerts.filter(a => a.entity === 'vacancy').length;
  const candidateAlerts = alerts.filter(a => a.entity === 'candidate').length;
  const entityChartData = [
    { name: 'Вакансии', count: vacancyAlerts },
    { name: 'Кандидаты', count: candidateAlerts },
  ];

  const filteredAlerts = alerts.filter(
    a => (filterType === 'all' || a.type === filterType) && (filterEntity === 'all' || a.entity === filterEntity)
  );

  return (
    <div style={{ padding: '0 20px 40px 20px', fontFamily: 'Inter, sans-serif' }}>
      
      {/* Шапка */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, #6a11cb, #2575fc)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 8px 20px rgba(106,17,203,0.3)' }}>
          <Bell size={24} />
        </div>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: '#1a1a2e', margin: 0 }}>Алерты и уведомления</h1>
          <p style={{ color: '#64748b', margin: '4px 0 0 0', fontSize: 14 }}>Следите за критическими событиями в рекрутинге</p>
        </div>
      </div>

      {/* KPI Карточки */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(37,117,252,0.1)', color: '#2575fc' }}><Bell size={22} /></div>
          <div><div style={styles.kpiLabel}>Всего</div><div style={styles.kpiValue}>{total}</div></div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(239,68,68,0.1)', color: '#ef4444' }}><AlertTriangle size={22} /></div>
          <div><div style={styles.kpiLabel}>Критических</div><div style={styles.kpiValue}>{criticalCount}</div></div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(245,158,11,0.1)', color: '#f59e0b' }}><AlertTriangle size={22} /></div>
          <div><div style={styles.kpiLabel}>Предупреждений</div><div style={styles.kpiValue}>{warningCount}</div></div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(59,130,246,0.1)', color: '#3b82f6' }}><Info size={22} /></div>
          <div><div style={styles.kpiLabel}>Информационных</div><div style={styles.kpiValue}>{infoCount}</div></div>
        </div>
      </div>

      {/* Графики */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
        <div style={styles.glassCard}>
          <h3 style={styles.sectionTitle}>Распределение по типам</h3>
          <div style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={typeChartData} innerRadius={60} outerRadius={90} paddingAngle={4} dataKey="value">
                  {typeChartData.map((entry, index) => <Cell key={index} fill={entry.color} />)}
                </Pie>
                <Tooltip
                  contentStyle={{ background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.5)' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div style={styles.glassCard}>
          <h3 style={styles.sectionTitle}>По сущностям</h3>
          <div style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={entityChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                <Tooltip
                  contentStyle={{ background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.5)' }}
                />
                <Bar dataKey="count" fill="url(#barGradient)" radius={[8, 8, 0, 0]} animationDuration={1000} />
                <defs>
                  <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6a11cb" />
                    <stop offset="100%" stopColor="#2575fc" />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Фильтры-таблетки */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 6, background: 'rgba(255,255,255,0.5)', padding: 4, borderRadius: 12, border: '1px solid rgba(255,255,255,0.6)' }}>
          {['all', 'critical', 'warning', 'info'].map(t => (
            <button
              key={t}
              onClick={() => setFilterType(t)}
              style={{
                padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: filterType === t ? '#2575fc' : 'transparent',
                color: filterType === t ? '#fff' : '#64748b',
                fontWeight: 600, fontSize: 13, transition: 'all 0.2s'
              }}
            >
              {t === 'all' ? 'Все типы' : t === 'critical' ? 'Критические' : t === 'warning' ? 'Предупреждения' : 'Информация'}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, background: 'rgba(255,255,255,0.5)', padding: 4, borderRadius: 12, border: '1px solid rgba(255,255,255,0.6)' }}>
          {['all', 'vacancy', 'candidate'].map(e => (
            <button
              key={e}
              onClick={() => setFilterEntity(e)}
              style={{
                padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: filterEntity === e ? '#2575fc' : 'transparent',
                color: filterEntity === e ? '#fff' : '#64748b',
                fontWeight: 600, fontSize: 13, transition: 'all 0.2s'
              }}
            >
              {e === 'all' ? 'Все сущности' : e === 'vacancy' ? 'Вакансии' : 'Кандидаты'}
            </button>
          ))}
        </div>
      </div>

      {/* Список алертов */}
      {filteredAlerts.length === 0 ? (
        <div style={{ ...styles.glassCard, textAlign: 'center', padding: 60 }}>
          <CheckCircle2 size={48} color="#10b981" style={{ marginBottom: 16, opacity: 0.8 }} />
          <p style={{ color: '#64748b', fontSize: 16, margin: 0 }}>Нет активных алертов — всё под контролем!</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filteredAlerts.map((alert, idx) => {
            const conf = alertConfig[alert.type];
            const isExpanded = expandedId === idx;
            return (
              <div
                key={idx}
                style={{
                  ...styles.alertCard,
                  borderLeft: `4px solid ${conf.color}`,
                }}
              >
                <div
                  onClick={() => setExpandedId(isExpanded ? null : idx)}
                  style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '18px 20px', cursor: 'pointer' }}
                >
                  <div style={{ width: 40, height: 40, borderRadius: 12, background: conf.bg, color: conf.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {conf.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <strong style={{ fontSize: 16, color: '#1a1a2e' }}>{alert.title}</strong>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: conf.bg, color: conf.color, fontWeight: 600 }}>{conf.label}</span>
                    </div>
                    <p style={{ margin: '4px 0 0 0', fontSize: 14, color: '#64748b' }}>{alert.message}</p>
                  </div>
                  <div style={{ color: '#94a3b8' }}>
                    {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </div>
                </div>

                {isExpanded && (
                  <div style={{ padding: '0 20px 20px 20px', borderTop: '1px solid rgba(0,0,0,0.05)', background: 'rgba(255,255,255,0.3)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16, marginBottom: 16 }}>
                      <div><div style={styles.detailLabel}>Дата создания</div><div style={styles.detailValue}>{new Date(alert.createdAt).toLocaleString('ru-RU')}</div></div>
                      <div><div style={styles.detailLabel}>Связанная сущность</div><div style={styles.detailValue}>{alert.entity === 'vacancy' ? 'Вакансия' : 'Кандидат'}</div></div>
                    </div>
                    <button
                      onClick={() => {
                        if (alert.entity === 'vacancy') navigate(`/app/recruitment-analytics/vacancies/${alert.entityId}`);
                        else navigate(`/app/recruitment-analytics/candidates/${alert.entityId}`);
                      }}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8, background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
                        color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 10, fontWeight: 600, cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(37,117,252,0.3)'
                      }}
                    >
                      Перейти к {alert.entity === 'vacancy' ? 'вакансии' : 'кандидату'} <ExternalLink size={16} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  glassCard: { background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: 24, padding: 24, border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 12px 40px rgba(31,38,135,0.06)' },
  kpiCard: { background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', borderRadius: 20, padding: 20, border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 8px 24px rgba(31,38,135,0.04)', display: 'flex', alignItems: 'center', gap: 16 },
  kpiIcon: { width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  kpiLabel: { fontSize: 13, color: '#64748b', fontWeight: 600, marginBottom: 4 },
  kpiValue: { fontSize: 24, fontWeight: 800, color: '#1a1a2e' },
  sectionTitle: { fontSize: 18, fontWeight: 700, color: '#1a1a2e', margin: '0 0 16px 0' },
  alertCard: { background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: 20, border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 8px 24px rgba(31,38,135,0.04)', overflow: 'hidden', transition: 'transform 0.2s, box-shadow 0.2s' },
  detailLabel: { fontSize: 12, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 },
  detailValue: { fontSize: 15, fontWeight: 500, color: '#1a1a2e' },
};



export default AlertsPage;
